import { motion } from 'framer-motion';
import { FC, MouseEvent, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';

import Icon from '@/components/AlertDialogs/Icon';
import Button from '@/components/Button';
import { Modal } from '@/components/Modal';
import Text from '@/components/Text';
import { Dispatch, RootState } from '@/utils/store';

export const StyledDialog = styled(motion.div)`
  width: 384px;
  border-radius: 4px;
  background: ${({ theme }) => theme.colors.white};
  padding: 56px 24px 40px;
  text-align: center;
  position: relative;

  > svg {
    margin-bottom: 16px;
  }
`;

const StyledText = styled(Text)`
  margin-top: 8px;
  white-space: pre-line;
`;

export const CloseButton = styled.button`
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: 0.2s ease-out background-color;
  top: 20px;
  position: absolute;
  right: 20px;
  color: ${({ theme }) => theme.colors.black};

  svg {
    margin: 0;
  }

  &:hover {
    background: ${({ theme }) => theme.colors.grayscaleLight};
  }
`;

export const Actions = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  grid-column-gap: 16px;
  margin-top: 32px;
  > button {
    padding: 0;
  }
`;

interface Props {
  state: Omit<RootState['alerts'], 'show'>;
}

const Dialog: FC<Props> = ({ state }) => {
  const dispatch = useDispatch<Dispatch>();
  const ref = useRef<HTMLDivElement>(null);

  const onClose = () => {
    dispatch.alerts.close();
  };

  const onCloseBtnClick = (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    onClose();
  };

  useEffect(() => {
    const portalContainer = ref.current?.parentElement?.parentElement;
    if (!portalContainer) return;

    const handleClick = (e: Event) => {
      if (!ref.current?.contains(e.target as Node)) onClose();
    };

    portalContainer.addEventListener('click', handleClick, true);
    return () => portalContainer.removeEventListener('click', handleClick, true);
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    const callback = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      onClose();
    };
    document.addEventListener('keydown', callback);
    return () => document.removeEventListener('keydown', callback);
    // eslint-disable-next-line
  }, []);

  return (
    <Modal zIndex={50}>
      <StyledDialog
        ref={ref}
        initial={{ scale: 0.3 }}
        animate={{ scale: 1 }}
        exit={{ scale: 0.3 }}
      >
        <CloseButton onClick={onCloseBtnClick}>
          <svg
            width="14"
            height="14"
            viewBox="0 0 14 14"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M13 1L1 13"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M13 13L1 1"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </CloseButton>
        <Icon type={state.icon} />
        <Text $type="heading2" as="h4">
          {state.title}
        </Text>
        {!!state.text && <StyledText>{state.text}</StyledText>}
        {state.actions && (
          <Actions>
            <Button
              onClick={dispatch.alerts.onConfirm}
              loading={state.actionIsLoading}
              disabled={state.actionIsLoading}
              type="button"
            >
              Yes
            </Button>
            <Button
              onClick={dispatch.alerts.close}
              color="secondary"
              type="button"
            >
              No
            </Button>
          </Actions>
        )}
      </StyledDialog>
    </Modal>
  );
};

export default Dialog;
